import { useEffect, useState, useContext } from 'react';
import axios from '@helpers/axios';
import * as Sentry from '@sentry/browser';
import { TeamContext } from '@services/Session/context';
import { isToday, isUpcoming, isPast } from '@helpers/dateUtils';
import { TeamGameResponse } from '@dto/api';

const useTeamGameResponses = (teamId: string, force = false) => {
  const [upcomingGames, setUpcomingGames] = useState<TeamGameResponse[]>([]);
  const [pastGames, setPastGames] = useState<TeamGameResponse[]>([]);
  const [todayGames, setTodayGames] = useState<TeamGameResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const teamStore = useContext(TeamContext);

  useEffect(() => {
    const fetchTeamGameResponses = async (currentTeamId: string) => {
      setLoading(true);
      await axios
        .get(`/v2/teams/${currentTeamId}/games`)
        .then((response) => {
          const games = response.data;

          const past = games.filter((x: TeamGameResponse) =>
            isPast(x.scheduleTsStart)
          );
          const upcoming = games.filter((x: TeamGameResponse) =>
            isUpcoming(x.scheduleTsStart)
          );

          const today = games.filter((x: TeamGameResponse) =>
            isToday(x.scheduleTsStart)
          );

          setPastGames(past);
          setUpcomingGames(upcoming);
          setTodayGames(today);
          teamStore.setSchedule({ past, upcoming, today });
          setLoading(false);
        })
        .catch((err) => {
          Sentry.captureException(err);
          setError(
            `Failed to fetch team games. Please try again. Error: ${err}`
          );
        })
        .finally(() => {
          setLoading(false);
        });
    };
    if (
      (teamStore.schedule.past.length === 0 &&
        teamStore.schedule.upcoming.length === 0 &&
        teamStore.schedule.today.length === 0) ||
      force
    ) {
      fetchTeamGameResponses(teamId);
    } else {
      const s = teamStore.schedule;
      const { past, upcoming, today } = s;
      setPastGames(past);
      setUpcomingGames(upcoming);
      setTodayGames(today);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  return { pastGames, upcomingGames, todayGames, loading, error };
};

export default useTeamGameResponses;
